/**
 * ARK navigation.
 *
 * <ark-nav>: Shop opens the mega panel on pointer rest or click; Escape, the scrim, a
 * click outside or focus leaving close it. The panel sits right under the header
 * wherever the header currently is (announcement bar, sticky state).
 *
 * <ark-mobile-nav>: the menu button opens a full-screen modal dialog (focus is trapped
 * and Escape closes it natively); choosing a link closes it.
 */

class ArkNav extends HTMLElement {
  #controller = new AbortController();
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    const { signal } = this.#controller;
    this.trigger = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="trigger"]'));
    this.panel = /** @type {HTMLElement | null} */ (this.querySelector('[ref="panel"]'));
    this.scrim = /** @type {HTMLElement | null} */ (this.querySelector('[ref="scrim"]'));
    if (!this.trigger || !this.panel) return;

    const fine = matchMedia('(hover: hover) and (pointer: fine)');
    const item = this.trigger.closest('li');

    this.trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), { signal });
    item?.addEventListener('pointerenter', () => fine.matches && this.#later(() => this.open(), 90), { signal });
    item?.addEventListener('pointerleave', () => fine.matches && this.#later(() => this.close(), 180), { signal });
    this.panel.addEventListener('pointerenter', () => clearTimeout(this.#timer), { signal });
    this.panel.addEventListener('pointerleave', () => fine.matches && this.#later(() => this.close(), 180), { signal });
    this.scrim?.addEventListener('click', () => this.close(), { signal });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.close();
          this.trigger?.focus();
        }
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        const next = /** @type {Node | null} */ (event.relatedTarget);
        if (this.isOpen && next && !this.contains(next)) this.close();
      },
      { signal }
    );
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (this.isOpen && !this.contains(/** @type {Node} */ (event.target))) this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get isOpen() {
    return this.panel ? !this.panel.hidden : false;
  }

  /**
   * @param {() => void} fn
   * @param {number} delay
   */
  #later(fn, delay) {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(fn, delay);
  }

  open() {
    if (!this.panel || this.isOpen) return;
    const header = this.closest('header-component, .header, header');
    const bottom = header ? header.getBoundingClientRect().bottom : 64;
    this.style.setProperty('--ark-mega-top', `${Math.max(0, Math.round(bottom))}px`);
    this.panel.hidden = false;
    if (this.scrim) this.scrim.hidden = false;
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  close() {
    clearTimeout(this.#timer);
    if (!this.panel || !this.isOpen) return;
    this.panel.hidden = true;
    if (this.scrim) this.scrim.hidden = true;
    this.trigger?.setAttribute('aria-expanded', 'false');
  }
}

class ArkMobileNav extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const open = this.querySelector('[ref="open"]');
    const close = this.querySelector('[ref="close"]');
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
    if (!open || !this.dialog) return;

    open.addEventListener('click', () => this.open(), { signal });
    close?.addEventListener('click', () => this.dialog?.close(), { signal });
    this.dialog.addEventListener('click', (event) => {
      if (/** @type {Element} */ (event.target).closest('a[href]')) this.dialog?.close();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.removeProperty('overflow');
      open.setAttribute('aria-expanded', 'false');
      /** @type {HTMLElement} */ (open).focus({ preventScroll: true });
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.querySelector('[ref="open"]')?.setAttribute('aria-expanded', 'true');
  }
}

if (!customElements.get('ark-nav')) customElements.define('ark-nav', ArkNav);
if (!customElements.get('ark-mobile-nav')) customElements.define('ark-mobile-nav', ArkMobileNav);
